import { SearchMode } from "@common/enum";
import { IDictionary, IMessage, TripType } from "@common/models";
import { CreateStep } from "@components/Stepper";
import { ISizing, IViewport } from "@config/appConfig";
import { IFieldValidationMessage, IModelValidationMessage, IValidationError } from "@core/index";
import { AppAction, IAppAction } from "@misc/Action";
import { store } from "@misc/Reducer";
import Config from "@service/Config";
import Notification from "@service/Notification";
import { IOptionsResizer } from "@components/general/form/FileInput";
import { convertingNodesToArray } from "@pages/RouteDemonstration";
import Resizer from "react-image-file-resizer";

export const cabinClassColor: any = {
    0: "blue",
    1: "secondary",
    2: "primary"
}

export function validationMessage(messages: IModelValidationMessage, validationErrors: IValidationError): IFieldValidationMessage {
    const appliedMessages: any = {};
    for (let fieldNames = Object.keys(validationErrors), i = 0, il = fieldNames.length; i < il; ++i) {
        const fieldName = fieldNames[i];
        const failedRule = validationErrors[fieldName];
        appliedMessages[fieldName] = fieldName in messages ? messages[fieldName][failedRule] : null;
    }
    return appliedMessages;
}

export function colorDetection(id: number) {
    switch (id) {
        case 0:
            return "blue";
        case 1:
            return "primary";
        case 2:
            return "danger";
        default:
            return "primary";
    }
}

export function hexToRgb(hex: string) {
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
}

export function rgbToHex(r: number, g: number, b: number) {
    return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);

    function componentToHex(c: number) {
        var hex = c.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
    }
}


export function getAdditionalDate() {
    const width = getWidth();
    const { small, medium } = Config.get<IViewport>("viewport");
    let additionalNumber = 0;
    if (width > medium) {
        additionalNumber = 3;
    } else if (width > small && width <= medium) {
        additionalNumber = 2;
    } else if (width <= small) {
        additionalNumber = 1;
    }
    return additionalNumber;
}

export function getWidth() {
    let width = 0;
    if (typeof (window.innerWidth) === 'number') {
        width = window.innerWidth;

    } else {
        if (document.documentElement && (document.documentElement.clientWidth || document.documentElement.clientHeight)) {
            width = document.documentElement.clientWidth;

        } else {
            if (document.body && (document.body.clientWidth || document.body.clientHeight)) {
                width = document.body.clientWidth;
            }
        }
    }
    return width;
}

export function clearStoreForSearch(searchMode: SearchMode, completeReset: boolean, step?: number) {
    store.dispatch<IAppAction>({ type: AppAction.AirportTransfers, payload: { airportTransfers: [] } });
    store.dispatch<IAppAction>({ type: AppAction.AvailabilityResponse, payload: { availabilityResponse: {} } });
    store.dispatch<IAppAction>({ type: AppAction.SelectedFlights, payload: { selectedFlights: [] } });
    store.dispatch<IAppAction>({ type: AppAction.PriceRequest, payload: { priceRequest: {} } });
    store.dispatch<IAppAction>({ type: AppAction.PriceResponse, payload: { priceResponse: {} } });
    store.dispatch<IAppAction>({ type: AppAction.ShowRetryButton, payload: { showRetryButton: false } });
    store.dispatch<IAppAction>({ type: AppAction.AncillaryAvailabilityRequest, payload: { ancillaryAvailabilityRequest: {} } });
    store.dispatch<IAppAction>({ type: AppAction.AncillaryAvailabilityResponse, payload: { ancillaryAvailabilityResponse: {} } });
    store.dispatch<IAppAction>({ type: AppAction.SelectedAncillaries, payload: { selectedAncillaries: [] } });
    store.dispatch<IAppAction>({ type: AppAction.MissedAncillaries, payload: { missedAncillaries: [] } });
    store.dispatch<IAppAction>({ type: AppAction.ValidationErrors, payload: { validationErrors: [] } });
    store.dispatch<IAppAction>({ type: AppAction.CurrentStep, payload: { currentStep: step || CreateStep.Search } });
    store.dispatch<IAppAction>({ type: AppAction.SearchMode, payload: { searchMode } });
    store.dispatch<IAppAction>({ type: AppAction.BookingTransactionTimeout, payload: { bookingTransactionTimeout: { start: false } } });
    if (searchMode === SearchMode.Create) {
        store.dispatch<IAppAction>({ type: AppAction.Passengers, payload: { passengers: [] } });
        store.dispatch<IAppAction>({ type: AppAction.PesonList, payload: { personList: [] } });
        store.dispatch<IAppAction>({ type: AppAction.Contacts, payload: { contacts: [] } });
        store.dispatch<IAppAction>({ type: AppAction.PnrInfo, payload: { pnrInfo: {} } });
        store.dispatch<IAppAction>({ type: AppAction.ContactRefNumber, payload: { contactRefNumber: "-1" } });
    }
    if (completeReset) {
        store.dispatch<IAppAction>({
            type: AppAction.AvailabilityRequest, payload: {
                availabilityRequest: {
                    originDestionations: [{}],
                    paxTypeQuantity: {
                        adt: 1,
                        chd: 0,
                        inf: 0,
                        yad: 0
                    },
                    tripType: TripType.OneWay
                }
            }
        });
    }
}

interface IRenameKey {
    key: string;
    newKey: string;
}

export function renameKey(object: any, keys: IRenameKey[]) {
    if (!object)
        return null;

    const clonedObj = Object.assign({}, object);
    for (const item of keys) {
        if (item.key in clonedObj) {
            const targetKey = clonedObj[item.key];
            delete clonedObj[item.key];
            clonedObj[item.newKey] = targetKey;
        }
    }
    return clonedObj;
}

export function handleRSVError(messages: IMessage[]) {
    if (Array.isArray(messages))
        messages?.forEach(message => Notification(message));
}

export function onUnload(e: any) {
    e.preventDefault();
    e.returnValue = '';
}

export function searchInAllProperties(record: any, term: string) {
    for (const property in record)
        if (record[property] && typeof record[property] !== "object" && String(record[property]).toLowerCase().indexOf(term?.toLowerCase()) !== -1)
            return true;
    return false;
}

export function DigitsToEN(str: string) {
    const persianNumbers = [/۰/g, /۱/g, /۲/g, /۳/g, /۴/g, /۵/g, /۶/g, /۷/g, /۸/g, /۹/g];
    const arabicNumbers = [/٠/g, /١/g, /٢/g, /٣/g, /٤/g, /٥/g, /٦/g, /٧/g, /٨/g, /٩/g];
    for (let i = 0; i < 10; i++) {
        str = str.replace(persianNumbers[i], i.toString()).replace(arabicNumbers[i], i.toString());
    }
    return str;
}

export function generateText(record: any, expr: string) {
    if (!record)
        return;
    let params = expr.match(/{.+?}/g);
    if (!params)
        return record[expr];
    else {
        let result = expr;
        params.forEach(param => {
            const propertyName = param.substring(1, param.length - 1);
            const searchValue = new RegExp(param, "g");
            result = result.replace(searchValue, record[propertyName]);
        });
        return result;
    }
}

export function getReadableFileSizeString(fileSizeInBytes: number = 0): string {
    let i = -1;
    let byteUnits = [' kB', ' MB', ' GB', ' TB', ' PB', ' EB', ' ZB', ' YB'];
    do {
        fileSizeInBytes = fileSizeInBytes / 1024;
        i++;
    } while (fileSizeInBytes > 1024);

    return Math.max(fileSizeInBytes, 0.1).toFixed(0) + byteUnits[i];
};

export function scrollParentToChild(parent: HTMLElement, child: HTMLElement) {

    // Where is the parent on page
    const parentRect = parent.getBoundingClientRect();
    // What can you see?
    const parentViewableArea = {
        height: parent.clientHeight,
        width: parent.clientWidth
    };

    // Where is the child
    const childRect = child.getBoundingClientRect();
    // Is the child viewable?
    const isViewable = (childRect.top >= parentRect.top) && (childRect.bottom <= parentRect.top + parentViewableArea.height);

    // if you can't see the child try to scroll parent
    if (!isViewable) {
        // Should we scroll using top or bottom? Find the smaller ABS adjustment
        const scrollTop = childRect.top - parentRect.top;
        const scrollBot = childRect.bottom - parentRect.bottom;
        if (Math.abs(scrollTop) < Math.abs(scrollBot)) {
            // we're near the top of the list
            parent.scrollTop += (scrollTop - (parentViewableArea.height / 2));
        } else {
            // we're near the bottom of the list
            parent.scrollTop += (scrollBot + (parentViewableArea.height / 2));
        }
    }

}

export function getKeyByValue(object: IDictionary<Object>, value: any): string {
    return Object.keys(object).find(key => object[key] === value) || "";
}

export function elementInViewport(el: HTMLElement | null) {
    if (el) {
        var top = el.offsetTop;
        var left = el.offsetLeft;
        var width = el.offsetWidth;
        var height = el.offsetHeight;

        while (el.offsetParent) {
            el = el.offsetParent as HTMLElement;
            top += el.offsetTop;
            left += el.offsetLeft;
        }

        return (
            top < (window.pageYOffset + window.innerHeight) &&
            left < (window.pageXOffset + window.innerWidth) &&
            (top + height) > window.pageYOffset &&
            (left + width) > window.pageXOffset
        );
    }
    return false;
}

export function distinctValue<T>(value: T, index: number, self: T[]) {
    return self.indexOf(value) === index;
}

export function capitalizeFirstLetter(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export function nameof<TObject>(key: keyof TObject): string;
export function nameof<TObject>(obj: TObject, key: keyof TObject): string;
export function nameof(key1: any, key2?: any): any {
    return key2 ?? key1;
}

export function resizeFile(file: File | Blob, options: IOptionsResizer): Promise<File | string> {
    return new Promise((resolve) => {
        Resizer.imageFileResizer(
            file,
            options.width,
            options.height,
            options.format,
            options.quality,
            options.rotation,
            (file) => {
                resolve(file as File | string);
            },
            options.outputFormat
        );
    });
}

export const query = <T extends object>(target: T, query: string): boolean => {
    const result = [];
    let targets = [target];
    while (targets.length) {
        targets?.forEach(target => Object?.values(target)?.filter(value => typeof (value) === 'string' || typeof (value) === 'number')?.forEach(value => {
            const stringTarget: string = typeof (value) === 'number' ? String(value) : value;
            if (stringTarget.includes(query))
                result.push(value);
        }))
        let tmp: T[] = [];
        targets.forEach(target => Object.values(target).filter(value => (typeof (value) === 'object' && value !== null)).forEach(value => tmp.push(value)));
        targets = tmp;
    }
    return result.length ? true : false;
}

export const walkingOnElementChildren = (regionElement: ChildNode): ChildNode[] => {
    const elements: ChildNode[] = [];
    let targetElements = convertingNodesToArray(regionElement?.childNodes);
    let continuing = convertingNodesToArray(regionElement?.childNodes).length ? true : false;
    while (continuing) {
        const newTarget: ChildNode[] = []
        continuing = targetElements.some(element => convertingNodesToArray(element?.childNodes).length);
        targetElements?.forEach(element => {
            elements.push(element);
            convertingNodesToArray(element?.childNodes)?.forEach(element => newTarget.push(element));
        });
        targetElements = newTarget;
    }
    return elements;
}

export const setKeyCamelCaseToDiffCase = (str: string, model: "kebabCase" | "snakeCase"): string => {
    const kebabCase = str?.replace(/([A-Z])/g, " $1").toLowerCase().split(" ").join("_");
    switch (model) {
        case "kebabCase":
            return kebabCase?.replace('_', '-')
        case "snakeCase":
            return kebabCase
    }
}

export const camelize = (str: string): string => {
    return str.replace(/(?:^\w|[A-Z]|\b\w)/g, function(word, index) {
        return index === 0 ? word.toLowerCase() : word.toUpperCase();
      }).replace(/\s+/g, '');
}

export const convertToPlain = (html?: string) => {

    // Create a new div element
    var tempDivElement = document.createElement("div");

    // Set the HTML content with the given value
    tempDivElement.innerHTML = html!;
    const result = tempDivElement?.textContent || tempDivElement?.innerText || "";
    tempDivElement.remove();
    // Retrieve the text property of the element 
    return result;
}

// check type of device
export const isMobile = (): boolean => {
    return /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigator.userAgent)
        || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\\-(n|u)|c55\/|capi|ccwa|cdm\\-|cell|chtm|cldc|cmd\\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\\-s|devi|dica|dmob|do(c|p)o|ds(12|\\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\\-|_)|g1 u|g560|gene|gf\\-5|g\\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\\-(m|p|t)|hei\\-|hi(pt|ta)|hp( i|ip)|hs\\-c|ht(c(\\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\\-(20|go|ma)|i230|iac( |\\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\\-[a-w])|libw|lynx|m1\\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\\-2|po(ck|rt|se)|prox|psio|pt\\-g|qa\\-a|qc(07|12|21|32|60|\\-[2-7]|i\\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\\-|oo|p\\-)|sdk\/|se(c(\\-|0|1)|47|mc|nd|ri)|sgh\\-|shar|sie(\\-|m)|sk\\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\\-|v\\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\\-|tdg\\-|tel(i|m)|tim\\-|t\\-mo|to(pl|sh)|ts(70|m\\-|m3|m5)|tx\\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\\-|your|zeto|zte\\-/i.test(navigator.userAgent.substring(0, 4));
}

export function isSafariBrowser() {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.indexOf('safari') !== -1)
        if (userAgent.indexOf('chrome') > -1)
            // User Agent: Chrome
            return false
        else
            // User Agent: Safari
            return true
}

export function setKeyPascalCaseToDiffCase(model: "kebabCase" | "snakeCase", str: string): string {
    let kebabCase = str?.replace(/([A-Z])/g, " $1").toLowerCase().split(" ").join("_");
    if (kebabCase[0] === "_")
        kebabCase = kebabCase?.replace(kebabCase[0], "")
    switch (model) {
        case "kebabCase":
            return kebabCase?.replace('_', '-')
        case "snakeCase":
            return kebabCase
    }
}

export const createDataGrid = <T extends Object>(dataSource: T[], orderFormat: Array<keyof T> = Object.keys(dataSource[0]) as Array<keyof T>, emptyValue: any = " ") => {
    const removePrivate = dataSource;
    const keys = Object.keys(removePrivate[0]);
    const removeItem = keys.filter(item => !(item as string)?.includes("_"))
    const series = removeItem.map(key => {
        const order = orderFormat?.indexOf(key as keyof T)
        const first = order === 0 ? true : false;
        const end = order === removeItem.length - 1 ? true : false;
        const values = dataSource.map(item => {
            const keyOfItem = key as keyof T
            return item[keyOfItem] ?? emptyValue
        })
        return {
            label: key as keyof T,
            values,
            order,
            first,
            last: end
        }
    })
    return series
}

export const range = (start: number, stop?: number, step?: number) => {
    if (typeof stop == 'undefined') {
        // one param defined
        stop = start;
        start = 0;
    }

    if (typeof step == 'undefined') {
        step = 1;
    }

    if ((step > 0 && start >= stop) || (step < 0 && start <= stop)) {
        return [];
    }

    var result = [];
    for (var i = start; step > 0 ? i < stop : i > stop; i += step) {
        result.push(i);
    }

    return result;
};

export const setModeOfEnum = (str: string, data: any): number => {
    const result = Object.values(data)?.find(x => data[Number(x)] === str);
    return result as number
}

export const getSizingDefaultValues = (): ISizing => {
    const width = window.innerWidth;
    if (width <= Config.config.viewport.small)
        return {
            top: "20px",
            bottom: "20px"
        }
    else if (width > Config.config.viewport.small && width <= Config.config.viewport.medium)
        return {
            top: "30px",
            bottom: "30px"
        }
    else
        return {
            top: "40px",
            bottom: "40px"
        }
}